import { GridConfig } from "../GridConfig";

import { DiagramEventEmitter } from "../../../../diagram/modules/DiagramEventEmitter";
import { DIAGRAM_EMIT_EVENTS } from "../../../../diagram/modules/DiagramEvents";
import { PluginRetrievalUtils } from "../../utils/PluginRetrievalUtils";
import { CellRetrievalUtils } from "../../utils/CellRetrievalUtils";
import { SelectionWrapper } from "../selection/SelectionWrapper";
import { getInstanceIDFromCanvasInstance } from "../../../../diagram/diagram-assets/diagram-positioning-state";

/**
 * @description Handles cell independent plugins
 */
export class CellIndependentPluginHandler {
    plugins = new Map();

    gridConfig;

    eventEmitter;

    currentlySelected = null;

    selectedParent;

    markedAsInvalidHover = [];

    cellRetrieval = new CellRetrievalUtils();

    ctx;

    constructor(instanceId) {
        this.instanceId = instanceId;
        this.gridConfig = new GridConfig(this.instanceId);
        this.eventEmitter = new DiagramEventEmitter(this.instanceId);
    }

    /**
     * @description sets the ctx
     * @param {Object} ctx - canvas 2d ctx
     */
    setCtx(ctx) {
        this.ctx = ctx;
    }

    /**
     * @description Creates the dropped plugin
     * @param {Function} PluginFactory
     * @param {String} pluginId
     * @param {Object} dragVal - {width,height} of the plugin
     * @param {Array[][]} grid - generated grid matrix
     * @param {Object} ctx - Canvas 2d context
     */
    createDroppedPlugin(PluginFactory, pluginId, dragVal, grid, ctx) {
        const position = this.getStartPointFromCoordonates(dragVal, grid);
        const size = this.getPluginSize(dragVal);
        const invalidDrop = this.getCoveredCells(position, size, grid, true).filter(cell => cell.hasContent() || cell.isVoid()).length > 0;
        if (invalidDrop) {
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.INVALID_PLUGIN_DROP, { PLUGIN_ID: pluginId });
            return;
        }
        this.createPlugin(PluginFactory, pluginId, position, size, grid, ctx);
    }

    /**
     * @description - Creates the plugin
     * @param {Function} PluginFactory
     * @param {string} pluginId
     * @param {Object} position  {x,y}
     * @param {Object} size  {width,height}
     * @param {array[][]} grid
     * @param {Object} ctx  - canvas 2d ctx
     * @param {Object} data - initial plugin data
     * @param {Object} state - initial plugin state
     */
    createPlugin(PluginFactory, pluginId, position, size, grid, ctx, data, state, isDataModel, oldState) {
        state = state || {};
        data = data || {};
        const instance = new PluginFactory({ position, size }, ctx, { id: pluginId, ...data }, { ...state });
        const coveredCells = this.getCoveredCells(position, size, grid, true);
        const coveredCellsAndAdjacent = this.getCoveredCells(position, size, grid);
        if (!isDataModel) this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CELL_INDEPENDENT_CONTAINER_ADDED, { pluginUUID: instance.uuid }, true, true);

        this.plugins.set(instance.uuid, {
            instance,
            pluginId,
            coveredCells,
            coveredCellsAndAdjacent
        });
        this.markCellsAsVoidOrUnvoid(coveredCells, true);
        instance.draw();
        this.getSelectionWrapperInstance(ctx).setSelected("CELL_INDEP", { parent: instance.uuid });
        if (!isDataModel) this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CELL_INDEPENDENT_CONTAINER_ADDED, { pluginUUID: instance.uuid }, null, true);
        if (!isDataModel) {
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.MULTIPLE_ACTIONS_HAPPENED, [
                { event: DIAGRAM_EMIT_EVENTS.CELL_INDEPENDENT_CONTAINER_ADDED, oldState, payload: { PLUGIN_ID: instance.data.id, PLUGIN_UUID: instance.uuid, PLUGIN_LABEL: instance.data.label } }
            ], true, false);
        }
    }

    /**
     * @description returns a selectionWrapper instance
     * @param {Object} ctx  - canvas 2d context
     */
    getSelectionWrapperInstance(ctx) {
        const selectionWrapper = new SelectionWrapper(getInstanceIDFromCanvasInstance(ctx));
        selectionWrapper.setCellIndependentHandler(this);
        return selectionWrapper;
    }

    /**
     * @description handles the redrawing of all affected (by the cell redrawing) cellindependent plugins
     * @param {GridCell} cell - the redrawed cell
     */
    cellRedrawed(cell) {
        const pluginKeys = this.plugins.keys();
        for (const key of pluginKeys) {
            const plugin = this.plugins.get(key);
            const isRedrawedIndex = plugin.coveredCellsAndAdjacent.indexOf(cell);
            if (isRedrawedIndex !== -1) {
                plugin.coveredCells.forEach(coveredCell => {
                    if (coveredCell && !coveredCell.isVoid()) coveredCell.draw(true);
                });
                plugin.instance.draw();
                break;
            }
        }
    }

    /**
     * @description  Handles the click selection on a cell independent plugin
     * @param {Object} payload - {x,y}
     * @param {Array[][]} grid - GeneratedGrid
     * @param {CellSelectio} cellSel -
     * @param {LineSelectio} lineSel
     */
    handleClickSelection(payload, grid, cellSel, lineSel) {
        const cellIndependentChildAction = this.childrenClickCheck(payload, grid);
        let noSelectionEventEmission = false;
        const classModelEditMode = this.checkIfEditClass(payload);
        if (this.selected && this.selectedParent && cellIndependentChildAction.pluginUuid && cellIndependentChildAction.childUuid
            && this.selected.uuid === cellIndependentChildAction.childUuid && this.selectedParent.uuid === cellIndependentChildAction.pluginUuid) {
            noSelectionEventEmission = true;
        }
        if (!cellIndependentChildAction.childUuid && !this.selectedParent && this.selected && this.selected.uuid === cellIndependentChildAction.pluginUuid) {
            noSelectionEventEmission = true;
        }
        if (cellIndependentChildAction && !noSelectionEventEmission && classModelEditMode) {
            this.getSelectionWrapperInstance(this.ctx).setSelected("CELL_INDEP", { parent: cellIndependentChildAction.pluginUuid,
                child: cellIndependentChildAction.childUuid });
        }

        if (cellIndependentChildAction.elem === "CONTAINER" && classModelEditMode) {
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.EDIT_CONTAINER, { pluginUuid: this.selected.uuid, label: this.selected.data.label });
        }
    }

    /**
     * @description Sets selected status on a cell independent plugin
     * @param {String} uuid - cell independent container uuid
     * @param {String } childUuid -  cell independent container child @Optional . If provided sets on the child selected status
     */
    setSelected(uuid, childUuid) {
        if (uuid && childUuid) {
            const container = this.plugins.get(uuid);
            if (!container) {
                return;
            }
            const child = container.instance.getChild(childUuid);
            if (!child) {
                console.error(`Child with uuid: ${childUuid}, inside container with uuid:${uuid}, was not found!`);
                return;
            }
            if (this.selected && child && this.selected === child) {
                return;
            }
            if (this.selected === child && this.selectedParent === container.instance) {
                return;
            }
            for (const key of this.plugins.keys()) {
                const { instance } = this.plugins.get(key);
                if (instance.state.childSelected && instance !== container.instance) {
                    instance.update({}, { childSelected: false });
                    instance.draw();
                }
            }
            container.instance.update({}, { childSelected: true });
            container.instance.draw();
            this.updateLastSelectedElement();
            this.selected = child;
            this.selectedParent = container.instance;
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.SELECTED_CONTAINER_CHILD, { pluginUuid: this.selectedParent.uuid, childPluginUuid: this.selected.uuid });
            this.updateNewlySelectedElement();
        } else if (uuid && !childUuid) {
            const container = this.plugins.get(uuid);
            if (!container) {
                console.error(`Container with uuid: ${uuid} was not found!`);
                return;
            }
            if (this.selected && container.instance && this.selected === container.instance) {
                return;
            }
            this.updateLastSelectedElement();
            if (this.selectedParent && this.selectedParent.state.childSelected) {
                this.selectedParent.update({}, { childSelected: false });
                this.selectedParent.draw();
            }
            this.selected = container.instance;
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.SELECTED_CONTAINER, { pluginUuid: this.selected.uuid });
            this.selectedParent = null;
            this.updateNewlySelectedElement();
        } else {
            if (this.selectedParent && this.selectedParent.state.childSelected) {
                this.selectedParent.update({}, { childSelected: false });
                this.selectedParent.draw();
            }
            this.updateLastSelectedElement();
            this.selected = null;
            this.selectedParent = null;
        }
    }

    /**
     * @description mouse move handler on a cell independent plugin
     */
    mouseMoveHandler(payload, draggedPlugin, grid) {
        const toIgnore = draggedPlugin ? [draggedPlugin.instance.uuid] : [];
        this.resetInvalidHover();
        const config = new GridConfig(this.instanceId);
        const position = {
            x: payload.x - (payload.x % config.cellWidth),
            y: payload.y - (payload.y % config.cellHeight)
        };
        const size = {
            width: (payload.width || draggedPlugin.instance.size.width),
            height: (payload.height || draggedPlugin.instance.size.height)
        };
        const occupiedCells = this.getCoveredCells(position, size, grid, true, false, true);
        if (occupiedCells.length > 0) {
            for (const key of this.plugins.keys()) {
                if (toIgnore.indexOf(key) !== -1) continue;
                const plugin = this.plugins.get(key);
                const cellInterception = plugin.coveredCells.filter(cell => occupiedCells.indexOf(cell) !== -1);
                if (cellInterception.length > 0) {
                    plugin.instance.update({}, { dragHover: true });
                    plugin.instance.draw();
                    this.markedAsInvalidHover.push(plugin);
                }
            }
        }
    }

    /**
     * @description resets the invalid hover status over a plugin
     */
    resetInvalidHover() {
        this.markedAsInvalidHover.forEach(plugin => {
            plugin.instance.update({}, { dragHover: false });
            plugin.instance.draw();
        });
        this.markedAsInvalidHover = [];
    }

    /**
     * @description handles the mouseup event
     */
    mouseup() {
        this.resetInvalidHover();
    }

    /**
     * @description handles the mouseleave event
     */
    mouseLeave() {
        this.resetInvalidHover();
    }

    /**
     * @description handles the mousedrop event
     */
    mouseDrop() {
        this.resetInvalidHover();
    }

    /**
     * @description returns the selected plugin
     */
    getSelected() {
        return this.selected;
    }

    /**
     * @description updates the last selected element selected value
     */
    updateLastSelectedElement() {
        if (!this.selected) return;
        this.updateElementSelectedStatus(this.selected, false);
    }

    /**
     * @description updates the newly selected element selected state value
     */
    updateNewlySelectedElement() {
        if (!this.selected) return;
        this.updateElementSelectedStatus(this.selected, true);
    }

    /**
     * @description updates and redraws the plugin
     * @param {Object} elem - cell independent plugin
     * @param {Boolean} selected - selected value to be set
     */
    updateElementSelectedStatus(elem, selected) {
        elem.update({}, { selected });
        elem.draw();
    }

    /**
     * @description Returns the covered cells by a cell independent plugin
     * @param {Object} position
     * @param {Object} size
     * @param {Array[][]} grid
     * @param {Boolean} exactSize - if it should look for the covered cells exactly with the provided values or with a 1 offset on the x and y
     * @param {Boolean} justPositions - if it should only return the positions of the covered cells
     */
    getCoveredCells(position, size, grid, exactSize, justPositions, checkIsVoid) {
        const cells = [];
        const startX = position.x;
        const startY = position.y;
        const endX = startX + size.width; // +1 for border
        const endY = startY + size.height; // +1 for border
        const horizontalCells = (endX - startX) / this.gridConfig.cellWidth + (exactSize ? 0 : 1);
        const verticalCells = (endY - startY) / this.gridConfig.cellHeight + (exactSize ? 0 : 1);
        for (let i = 0; i < verticalCells; i++) {
            for (let x = 0; x < horizontalCells; x++) {
                const currentPosition = {
                    x: startX + (x * this.gridConfig.cellWidth),
                    y: startY + (i * this.gridConfig.cellHeight)
                };
                let cell;
                if (justPositions) cell = this.cellRetrieval.getCellPosition(currentPosition, null, grid);
                else cell = this.cellRetrieval.getCell(this.cellRetrieval.getCellPosition(currentPosition, null, grid), grid);
                if (checkIsVoid) {
                    if (cell && cell.isVoid()) {
                        cells.push(cell);
                    }
                } else {
                    cells.push(cell);
                }
            }
        }
        return cells;
    }

    /**
     * @description Adds a child to a cell independent container
     */
    addChild(childData, fabricDiagramCtx, oldState) {
        const pluginRetrieval = new PluginRetrievalUtils();
        const childFactory = pluginRetrieval.getPluginFromDiagram({ pluginId: childData.childPluginID }, fabricDiagramCtx);
        const container = this.plugins.get(childData.containerUuid);

        if (!childFactory) {
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.API_CONTAINER_ADD_CHILD_ERROR, { reason: "UNEXISTING_CHILD_PLUGIN" });
            return;
        }
        if (!container) {
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.API_CONTAINER_ADD_CHILD_ERROR, { reason: "UNEXISTING_CONTAINER" });
            return;
        }
        this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONTAINER_CHILD_ADDED, { pluginUUID: childData.containerUuid }, true, true);
        const instance = container.instance.addChild(childData.state, childData.data, childFactory.plugin, childData.childPluginID);
        this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONTAINER_CHILD_ADDED, { pluginUUID: childData.containerUuid, childPluginUUID: instance.uuid }, false, true);

        this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.MULTIPLE_ACTIONS_HAPPENED, [
            { event: DIAGRAM_EMIT_EVENTS.CONTAINER_CHILD_ADDED, oldState, payload: { pluginUUID: childData.containerUuid, childPluginUUID: instance.uuid } }
        ], true, false);
        return instance.uuid;
    }

    /**
     * @description removes a container
     */
    removeContainer(uuid, oldState) {
        const plugin = this.plugins.get(uuid);
        const toRedraw = Object.assign([], plugin.coveredCells);
        const toMarkAsNotVoid = Object.assign([], plugin.coveredCells);

        this.getSelectionWrapperInstance(this.ctx).resetSpecific(["CELL_INDEP"]);
        // this.setSelected(null);
        plugin.instance.clear();
        this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CELL_INDEPENDENT_CONTAINER_REMOVED, { pluginUUID: uuid }, true, true);

        this.plugins.delete(uuid);
        this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CELL_INDEPENDENT_CONTAINER_REMOVED, { pluginUUID: uuid }, false, true);
        this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.MULTIPLE_ACTIONS_HAPPENED, [
            { event: DIAGRAM_EMIT_EVENTS.CELL_INDEPENDENT_CONTAINER_REMOVED, oldState, payload: { pluginUUID: uuid } }
        ], true, false);

        toMarkAsNotVoid.forEach(cell => {
            cell.update({}, { void: false });
            if (toRedraw.indexOf(cell) !== -1) cell.draw();
        });
    }

    /**
     * @description removes all cell independent plugins
     */
    removeAllCellIndependentPlugins() {
        this.plugins.forEach((value, key) => {
            this.removeContainer(key);
        });
    }

    /**
     * @description Updates a celll inpdenendent container data
     * @param {String} uuid  - the uuid of the container
     * @param {Object} data - data to be updated
     */
    updateContainerData(uuid, data, diagramContent) {
        const container = this.plugins.get(uuid);
        // let eventEmitter = new DiagramEventEmitter();
        if (!container) {
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.API_CONTAINER_ADD_CHILD_ERROR, { reason: "UNEXISTING_CONTAINER" });
            return;
        }
        container.instance.updateContainerData(data);
    }

    /**
     * @description returns the plugin size
     */
    getPluginSize(dragVal) {
        return { width: dragVal.width, height: dragVal.height };
    }

    /**
     * @description Returns a plugin with the provided uuid
     */
    getPlugin(uuid, instance) {
        for (const key of this.plugins.keys()) {
            if (key === uuid) return instance ? this.plugins.get(key).instance : { parent: key };
            const plugin = this.plugins.get(key);
            const child = plugin.instance.getChild(uuid);
            if (child) {
                if (instance) return child;
                return { parent: key, child: child.uuid };
            }
        }
        return null;
    }

    /**
     * @description Updates a independent container childs data
     */
    updateChildData(containerUuid, childUuid, data) {
        const container = this.plugins.get(containerUuid);
        // let eventEmitter = new DiagramEventEmitter();
        if (!container) {
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.API_CONTAINER_UPDATE_CHILD_ERROR, { reason: "UNEXISTING_CONTAINER" });
            return;
        }
        container.instance.updateChildData(containerUuid, childUuid, data);
    }

    /**
     * @description Removes a independent container child
     */
    removeChild(containerUuid, childUuid, oldState) {
        const container = this.plugins.get(containerUuid);
        // let eventEmitter = new DiagramEventEmitter();
        if (!container) {
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.API_CONTAINER_REMOVE_CHILD_ERROR, { reason: "UNEXISTING_CONTAINER" });
            return;
        }
        this.getSelectionWrapperInstance(this.ctx).resetSpecific(["CELL_INDEP"]);

        // this.setSelected(null);
        this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONTAINER_CHILD_REMOVED, { pluginUUID: containerUuid, childPluginUUID: childUuid }, true, true);

        const val = container.instance.removeChild(childUuid);
        if (val) {
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONTAINER_CHILD_REMOVED, { pluginUUID: containerUuid, childPluginUUID: childUuid }, false, true);
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.MULTIPLE_ACTIONS_HAPPENED, [
                { event: DIAGRAM_EMIT_EVENTS.CONTAINER_CHILD_REMOVED, oldState, payload: { pluginUUID: containerUuid, childPluginUUID: childUuid } }
            ], true, false);
        } else {
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.API_CONTAINER_REMOVE_CHILD_ERROR, { reason: "CHILD NOT FOUND", childUUID: childUuid, pluginUUID: containerUuid });
        }
    }

    /**
     * @description Identifies a parent uuid from the provided child uuid
     */
    getParentUuidFromChildUuid(childUuid) {
        const keys = this.plugins.keys();
        for (const key of keys) {
            const plugin = this.plugins.get(key);
            const child = plugin.instance.getChild(childUuid);
            if (child) return key;
        }
        return null;
    }

    /**
     * @description Gets the start x and y of a cell independent container
     */
    getStartPointFromCoordonates(dragVal, grid) {
        const restX = dragVal.x % this.gridConfig.cellWidth;
        const restY = dragVal.y % this.gridConfig.cellHeight;
        return { x: dragVal.x - restX, y: dragVal.y - restY };
    }

    /**
     * @description Marks the provided cells with void true or false. Void = A cell independent plugin covers the cell
     * @param {GridCell[]} cells - The cells to update state
     * @param {Boolean} coveredVal - the value to be set as void
     */
    markCellsAsVoidOrUnvoid(cells, coveredVal) {
        cells.forEach(cell => {
            if (!cell) return;
            cell.update({}, { void: coveredVal, selected: false, dragHover: false });
            cell.draw(null, true);
        });
    }

    /**
     * @description Checks if the provided payload coordodnates are on a expand point
     */
    checkIfIsExpandPoint(payload, grid) {
        const cellPos = this.cellRetrieval.getCellPosition(payload, null, grid);
        const cell = this.cellRetrieval.getCell(cellPos, grid);
        const pluginKeys = this.plugins.keys();
        for (const key of pluginKeys) {
            const plugin = this.plugins.get(key);
            if (plugin.coveredCells.indexOf(cell) !== -1) {
                if (plugin.instance.isExpandPoint(payload)) {
                    return plugin;
                }
            }
        }
        return false;
    }

    /**
     * @description Checks if the provided payload coordodnates are on more items blade
     */
    checkIfIsMoreItemsBlade(payload, grid) {
        const cellPos = this.cellRetrieval.getCellPosition(payload, null, grid);
        const cell = this.cellRetrieval.getCell(cellPos, grid);
        const pluginKeys = this.plugins.keys();
        for (const key of pluginKeys) {
            const plugin = this.plugins.get(key);
            if (plugin.coveredCells.indexOf(cell) !== -1) {
                plugin.instance.isPropMoreItems(payload);
            }
        }
    }

    /**
     * @description Checks if the provided payload coordodnates are on a menu point
     */
    checkIfOpenMenu(payload, grid) {
        const pluginKeys = this.plugins.keys();
        for (const key of pluginKeys) {
            const plugin = this.plugins.get(key);
            if (plugin.instance.isOpenMenuPoint(payload)) {
                return plugin;
            }
        }
        return false;
    }

    /**
    * @description Checks if the provided payload coordodnates are on a edit point
    */
    checkIfEditClass(payload) {
        const pluginKeys = this.plugins.keys();
        for (const key of pluginKeys) {
            const plugin = this.plugins.get(key);
            if (plugin.instance.isEditPoint(payload)) {
                return plugin;
            }
        }
        return false;
    }

    /**
    * @description Returns the plugin instance that has its menu opened
    */
    checkForOpenedMenu() {
        const pluginKeys = this.plugins.keys();
        for (const key of pluginKeys) {
            const plugin = this.plugins.get(key);
            const headerInstance = plugin.instance.getHeaderInstance();
            if (headerInstance.menuIsOpen) {
                return headerInstance;
            }
        }
        return false;
    }

    /**
     * @description opens the header menu  on the provided  plugin instance
     * @param {Object} plugin - plugin instance
     * @param {*} payload
     */
    openHeaderMenu(plugin, payload) {
        const headerInstance = plugin.instance.getHeaderInstance();
        if (headerInstance && headerInstance.menuIsOpen) {
            headerInstance.closeImportExportMenu();
        }
        plugin.instance.getHeaderInstance().drawHeaderMenu(payload, this.plugins);
    }

    /**
     * @description Checks to see if the provided mouse position is hovering a cell independent plugin
     * @param {Object} mousePos  - {x,y}
     * @returns { Boolean }
     */
    mousePositionHoveringCellIndependentPlugin(mousePos) {
        const keys = this.plugins.values();
        let currentVal = null;
        do {
            currentVal = keys.next();
            if (currentVal && currentVal.value) {
                if (!currentVal.value.instance.isPointInsidePlugin) {
                    throw new Error('The cell independent plugin doesnt implement "isPointInsidePlugin" method');
                }
                const isInside = currentVal.value.instance.isPointInsidePlugin(mousePos);
                if (isInside) {
                    return true;
                }
            }
        }
        while (currentVal && currentVal.done !== true);
        return false;
    }

    /**
     * @description checks to see if the provided payload coordonates are on a move point
     */
    checkIfIsMovePoint(payload, grid) {
        const cellPos = this.cellRetrieval.getCellPosition(payload, null, grid);
        const cell = this.cellRetrieval.getCell(cellPos, grid);
        const pluginKeys = this.plugins.keys();
        for (const key of pluginKeys) {
            const plugin = this.plugins.get(key);
            if (plugin.coveredCells.indexOf(cell) !== -1) {
                if (plugin.instance.isMovePoint(payload)) {
                    return plugin;
                }
            }
        }
        return false;
    }

    /**
     * @description checks to see if the provided payload coordonates are on a child
     */
    childrenClickCheck(payload, grid) {
        const cellPos = this.cellRetrieval.getCellPosition(payload, null, grid);
        const cell = this.cellRetrieval.getCell(cellPos, grid);
        const pluginKeys = this.plugins.keys();
        for (const key of pluginKeys) {
            const plugin = this.plugins.get(key);
            if (plugin.coveredCells.indexOf(cell) !== -1) {
                const clickAction = plugin.instance.childrenClickCheck(payload);
                if (clickAction) return clickAction;
            }
        }
        return false;
    }
}
